import React, { useEffect, useState } from "react";
import { db } from "../../firebase/firebase";
import { doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";
import { FaBell, FaTrash, FaSearch, FaTimes } from "react-icons/fa";
import NotificationItem from "./NotificationItem";
import { IconWrapper } from "../../components/IconWrapper";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";

const NotificationMain = ({
  organizationId = "",
  color,
  bg,
  userId = "",
}: {
  organizationId: string;
  color: string;
  bg: string;
  userId: string;
}) => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("unread");
  const [timeFilter, setTimeFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("");

  useEffect(() => {
    if (!organizationId || !userId) return;

    const notifRef = doc(
      db,
      "organization",
      organizationId,
      "notifications",
      userId
    );

    const unsubscribe = onSnapshot(
      notifRef,
      (snapshot) => {
        setLoading(false);

        if (!snapshot.exists()) {
          setNotifications([]);
          return;
        }

        const raw = snapshot.data().messages || [];
        console.log("Notifications:", raw);

        const normalized = raw.map((n: any) => ({
          ...n,
          timestamp: n.timestamp?.toDate
            ? n.timestamp.toDate()
            : new Date(n.timestamp),
        }));

        normalized.sort(
          (
            a: { timestamp: { getTime: () => number } },
            b: { timestamp: { getTime: () => number } }
          ) => b.timestamp.getTime() - a.timestamp.getTime()
        );

        setNotifications(normalized);
      },
      (error) => {
        console.error("Notifications listener error:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [organizationId, userId]);

  const markAsRead = async (index: number) => {
    if (!organizationId || !userId) {
      console.error(
        "Cannot mark as read: organizationId or userId is missing."
      );
      return;
    }

    try {
      // Update local state first to preserve already-normalized Date objects
      const updatedNotifications = notifications.map((n, i) =>
        i === index ? { ...n, read: true } : n
      );

      setNotifications(updatedNotifications);

      await updateDoc(
        doc(db, `organization/${organizationId}/notifications/${userId}`),
        { messages: updatedNotifications }
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  

  const deleteNotification = async (index: number) => {
    if (!organizationId || !userId) {
      return;
    }
    try {
      const updatedNotifications = notifications.filter((_, i) => i !== index);

      setNotifications(updatedNotifications);

      await updateDoc(
        doc(db, `organization/${organizationId}/notifications/${userId}`),
        { messages: updatedNotifications }
      );
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const markAllAsRead = async () => {
    if (!organizationId || !userId) {
      return;
    }
    try {
      const updatedNotifications = notifications.map((notif) => ({
        ...notif,
        read: true,
      }));

      await updateDoc(
        doc(db, `organization/${organizationId}/notifications/${userId}`),
        {
          messages: updatedNotifications,
        }
      );

      setNotifications(updatedNotifications);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const deleteAllRead = async () => {
    if (!organizationId || !userId) {
      return;
    }
    try {
      const updatedNotifications = notifications.filter((notif) => !notif.read);

      await updateDoc(
        doc(db, `organization/${organizationId}/notifications/${userId}`),
        {
          messages: updatedNotifications,
        }
      );

      setNotifications(updatedNotifications);
    } catch (error) {
      console.error("Error deleting all read notifications:", error);
    }
  };

  const filterNotifications = (
    notifs: any[],
    timeFilter: string,
    projectFilter: string
  ) => {
    const now = new Date();
    return notifs.filter((notif) => {
      const notifDate = notif.timestamp;

      let timeMatch = true;
      if (timeFilter === "today") {
        timeMatch = notifDate.toDateString() === now.toDateString();
      } else if (timeFilter === "month") {
        timeMatch =
          notifDate.getMonth() === now.getMonth() &&
          notifDate.getFullYear() === now.getFullYear();
      } else if (timeFilter === "yearly") {
        timeMatch = notifDate.getFullYear() === now.getFullYear();
      }

      const projectMatch =
        projectFilter.trim() === "" ||
        (notif.projectName &&
          notif.projectName
            .toLowerCase()
            .includes(projectFilter.toLowerCase()));

      return timeMatch && projectMatch;
    });
  };

  // Create a stable key for each notification to prevent React from reusing state
  const getNotificationKey = (n: any) => {
    const ts = n?.timestamp
      ? n.timestamp.getTime?.() ?? Number(n.timestamp)
      : 0;
    return `${ts}-${n?.projectName ?? ""}-${n?.message ?? ""}`;
  };

  const unreadNotifications = filterNotifications(
    notifications.filter((notif) => !notif.read),
    timeFilter,
    projectFilter
  );
  const readNotifications = filterNotifications(
    notifications.filter((notif) => notif.read),
    timeFilter,
    projectFilter
  );

  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  return (
    <div className="w-full py-4 px-6 flex flex-col">
      {/* Header and Tabs */}
      <div className={`flex items-center justify-between mb-4 border-b pb-2 ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
        <div className="flex items-center">
          <IconWrapper
            icon={FaBell}
            size={24}
            className={`${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'} mr-3`}
          />
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Notifications</h2>
        </div>

        {/* Tabs for Unread & Read */}
        <div className="flex space-x-4">
          <button
            className={`px-3 py-1 font-semibold text-sm transition-all duration-200 ${
              activeTab === "unread"
                ? `${isDark ? 'border-b-2 border-[#B59143] text-[#FBD282]' : 'border-b-2 border-[#B59143] text-[#B59143]'}`
                : `${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`
            }`}
            onClick={() => {
              setActiveTab("unread");
              setTimeFilter("all");
            }}
          >
            Unread
            {unreadNotifications.length > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 text-xs font-medium rounded-full ${isDark ? 'bg-[#2A3441] text-white' : 'bg-[#FFF7E6] text-[#B59143] border border-[#E8C16C]'}`}>
                {unreadNotifications.length}
              </span>
            )}
          </button>
          <button
            className={`px-3 py-1 font-semibold text-sm transition-all duration-200 ${
              activeTab === "read"
                ? `${isDark ? 'border-b-2 border-[#B59143] text-[#FBD282]' : 'border-b-2 border-[#B59143] text-[#B59143]'}`
                : `${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`
            }`}
            onClick={() => {
              setActiveTab("read");
              setTimeFilter("all");
            }}
          >
            Read
            {readNotifications.length > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 text-xs font-medium rounded-full ${isDark ? 'bg-[#2A3441] text-white' : 'bg-[#FFF7E6] text-[#B59143] border border-[#E8C16C]'}`}>
                {readNotifications.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Tab Panels */}
      <div className="w-full">
        {/* Unread Notifications Panel */}
        {activeTab === "unread" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              {/* Project Name Filter Input - Left Side */}
              <div className={`flex items-center border rounded-md w-64 pr-1 ${isDark ? 'border-[#2E2F33] focus-within:ring-1 focus-within:ring-[#B59143]' : 'border-gray-300 focus-within:border-[#B59143] focus-within:ring-1 focus-within:ring-[#B59143]'}`}>
                <IconWrapper
                  icon={FaSearch}
                  size={16}
                  className={`ml-3 mr-2 ${isDark ? 'text-[#C3C9D3]' : 'text-gray-400'}`}
                />
                <input
                  type="text"
                  placeholder="Filter by project name..."
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                  className={`flex-grow py-1 text-sm focus:outline-none ${isDark ? 'text-white placeholder-[#C3C9D3] bg-transparent' : 'text-gray-900 placeholder-gray-500'}`}
                />
                {projectFilter && (
                  <button
                    onClick={() => setProjectFilter("")}
                    className={`p-1 mr-1 focus:outline-none ${isDark ? 'text-[#C3C9D3] hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                    aria-label="Clear project filter"
                    title="Clear"
                  >
                    <IconWrapper icon={FaTimes} size={12} className="w-3 h-3" />
                  </button>
                )}
              </div>
              {/* Container for Time Filter and Mark All as Read button - Right Side */}
              <div className="flex items-center gap-4">
                {/* Time Filter Dropdown */}
                <select
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value)}
                  className={`px-3 py-1 border rounded-md text-sm focus:outline-none ${isDark ? 'text-white bg-[#14161B] border-[#2E2F33] focus:ring-[#B59143] focus:border-[#B59143]' : 'text-gray-900 border-gray-300 focus:ring-[#B59143] focus:border-[#B59143]'}`}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="month">This Month</option>
                  <option value="yearly">This Year</option>
                </select>
                {/* Mark All as Read button */}
                {unreadNotifications.length > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${isDark ? 'text-black bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143]' : 'text-white bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143]'}`}
                  >
                    Mark All as Read
                  </button>
                )}
              </div>
            </div>
            {unreadNotifications.length === 0 ? (
              <div className={`mt-4 flex items-center justify-center h-[50vh] text-center text-md ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                No new notifications!
              </div>
            ) : (
              <>
                {/* Subtle divider */}
                <div className={`border-t my-4 ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}></div>
                <div className={`overflow-y-auto p-4 rounded-lg shadow-sm mt-4 ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white'}`}>
                  {unreadNotifications.map((notif, index) => (
                    <NotificationItem
                      key={getNotificationKey(notif)}
                      notification={notif}
                      index={notifications.findIndex(
                        (n) => getNotificationKey(n) === getNotificationKey(notif)
                      )}
                      markAsRead={markAsRead}
                      deleteNotification={deleteNotification}
                      color={isDark ? 'text-white' : 'text-gray-800'}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Read Notifications Panel */}
        {activeTab === "read" && (
          <div>
              <div className="flex items-center justify-between mb-4">
              {/* Project Name Filter Input - Left Side */}
              <div className={`flex items-center border rounded-md w-64 pr-1 ${isDark ? 'border-[#2E2F33] focus-within:ring-1 focus-within:ring-[#B59143]' : 'border-gray-300 focus-within:border-[#B59143] focus-within:ring-1 focus-within:ring-[#B59143]'}`}>
                <IconWrapper
                  icon={FaSearch}
                  size={16}
                  className={`ml-3 mr-2 ${isDark ? 'text-[#C3C9D3]' : 'text-gray-400'}`}
                />
                <input
                  type="text"
                  placeholder="Filter by project name..."
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                  className={`flex-grow py-1 text-sm focus:outline-none ${isDark ? 'text-white placeholder-[#C3C9D3] bg-transparent' : 'text-gray-900 placeholder-gray-500'}`}
                />
                {projectFilter && (
                  <button
                    onClick={() => setProjectFilter("")}
                    className={`p-1 mr-1 focus:outline-none ${isDark ? 'text-[#C3C9D3] hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                    aria-label="Clear project filter"
                    title="Clear"
                  >
                    <IconWrapper icon={FaTimes} size={12} className="w-3 h-3" />
                  </button>
                )}
              </div>
              {/* Container for Time Filter and Delete All button - Right Side */}
              <div className="flex items-center gap-4">
                {/* Time Filter Dropdown */}
                <select
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value)}
                  className={`px-3 py-1 border rounded-md text-sm focus:outline-none ${isDark ? 'text-white bg-[#14161B] border-[#2E2F33] focus:ring-[#B59143] focus:border-[#B59143]' : 'text-gray-900 border-gray-300 focus:ring-[#B59143] focus:border-[#B59143]'}`}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="month">This Month</option>
                  <option value="yearly">This Year</option>
                </select>
                {/* Delete All button */}
                {readNotifications.length > 0 && (
                  <button
                    onClick={deleteAllRead}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 flex items-center ${isDark ? 'text-black bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143]' : 'text-white bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143]'}`}
                  >
                    <IconWrapper
                      icon={FaTrash}
                      size={16}
                      className={`w-4 h-4 mr-2 ${isDark ? 'text-black' : 'text-white'}`}
                    />
                    Delete All
                  </button>
                )}
              </div>
            </div>
            {readNotifications.length === 0 ? (

              <div className={`mt-4 flex items-center justify-center h-[50vh] text-center text-md ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                No read notifications!
              </div>
            ) : (
              <>
                {/* Subtle divider */}
                <div className={`border-t my-4 ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}></div>
                <div className={`overflow-y-auto p-4 rounded-lg shadow-sm mt-4 ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white'}`}>
                  {readNotifications.map((notif, index) => (
                    <NotificationItem
                      key={getNotificationKey(notif)}
                      notification={notif}
                      index={notifications.findIndex(
                        (n) => getNotificationKey(n) === getNotificationKey(notif)
                      )}
                      markAsRead={markAsRead}
                      deleteNotification={deleteNotification}
                      color={isDark ? 'text-white' : 'text-gray-800'}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationMain;
